import { useState } from 'react'
import type { ConversationsState } from '@/hooks/useConversations'
import type { ConversationResponse } from '@/lib/api'
import { formatRelativeTime } from '@/lib/relativeTime'
import styles from './ConversationSidebar.module.css'

const UNTITLED = 'Uten tittel'

export type ConversationSidebarProps = {
  state: ConversationsState
  activeConversationId: string | null
  open: boolean
  onSelect: (id: string) => void
  onNewConversation: () => void
  onRename: (id: string, title: string) => void
  onDelete: (id: string) => void
  onClose: () => void
}

export default function ConversationSidebar({
  state,
  activeConversationId,
  open,
  onSelect,
  onNewConversation,
  onRename,
  onDelete,
  onClose,
}: ConversationSidebarProps) {
  return (
    <>
      {/* Backdrop only renders at the mobile breakpoint (see module css);
       * clicking it closes the drawer. */}
      {open && <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />}
      <nav
        className={`${styles.sidebar} ${open ? styles.open : ''}`}
        aria-label="Samtaler"
        aria-hidden={!open}
      >
        <button className={styles.newButton} onClick={onNewConversation}>
          + Ny samtale
        </button>
        {renderList(state, activeConversationId, onSelect, onRename, onDelete)}
      </nav>
    </>
  )
}

function renderList(
  state: ConversationsState,
  activeConversationId: string | null,
  onSelect: (id: string) => void,
  onRename: (id: string, title: string) => void,
  onDelete: (id: string) => void,
) {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return <p className={styles.muted}>Laster samtaler…</p>
  }
  if (state.kind === 'error') {
    return <p className={styles.error}>Kunne ikke laste samtaler.</p>
  }
  if (state.conversations.length === 0) {
    return <p className={styles.muted}>Ingen samtaler ennå.</p>
  }
  return (
    <ul className={styles.list}>
      {state.conversations.map((conversation) => (
        <SidebarItem
          key={conversation.id}
          conversation={conversation}
          isActive={conversation.id === activeConversationId}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
    </ul>
  )
}

type SidebarItemProps = {
  conversation: ConversationResponse
  isActive: boolean
  onSelect: (id: string) => void
  onRename: (id: string, title: string) => void
  onDelete: (id: string) => void
}

function SidebarItem({ conversation, isActive, onSelect, onRename, onDelete }: SidebarItemProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [armedDelete, setArmedDelete] = useState(false)

  const label = conversation.title.trim() !== '' ? conversation.title : UNTITLED

  const commitRename = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== '' && trimmed !== conversation.title) {
      onRename(conversation.id, trimmed)
    }
  }

  if (editing) {
    return (
      <li className={`${styles.item} ${isActive ? styles.active : ''}`}>
        <input
          className={styles.renameInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename()
            if (e.key === 'Escape') setEditing(false)
          }}
          aria-label={`Nytt navn for ${label}`}
          /* The input appears on an explicit user action (clicked rename),
           * so moving focus into it is the expected behaviour. */
          autoFocus
        />
      </li>
    )
  }

  return (
    <li
      className={`${styles.item} ${isActive ? styles.active : ''}`}
      onMouseLeave={() => setArmedDelete(false)}
    >
      <button
        className={styles.select}
        aria-current={isActive ? 'true' : undefined}
        onClick={() => onSelect(conversation.id)}
      >
        <span className={styles.title}>{label}</span>
        <span className={styles.time}>{formatRelativeTime(conversation.updatedAt)}</span>
      </button>
      <div className={styles.actions}>
        <button
          className={styles.action}
          aria-label={`Gi nytt navn til ${label}`}
          title="Gi nytt navn"
          onClick={() => {
            setDraft(conversation.title)
            setArmedDelete(false)
            setEditing(true)
          }}
        >
          <PencilIcon />
        </button>
        {armedDelete ? (
          <button
            className={`${styles.action} ${styles.confirmDelete}`}
            aria-label={`Bekreft sletting av ${label}`}
            onClick={() => onDelete(conversation.id)}
          >
            Slett?
          </button>
        ) : (
          <button
            className={styles.action}
            aria-label={`Slett ${label}`}
            title="Slett samtale"
            onClick={() => setArmedDelete(true)}
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </li>
  )
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M11.1 2.2a1.4 1.4 0 0 1 2 2L5.4 11.9l-2.8.8.8-2.8 7.7-7.7Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M2.5 4.5h11M6.5 4.5v-1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1m2.5 0-.6 8a1 1 0 0 1-1 .9H5.6a1 1 0 0 1-1-.9l-.6-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
